import { Eye, Download, FileText, MessageSquare } from "lucide-react";
import { useAdmin } from "./AdminContext";

export default function AdminOverview() {
  const { stats, data } = useAdmin();

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Dashboard Overview</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="stats-card" data-testid="stat-protocols">
            <div className="stats-number">{stats?.protocol_count || 0}</div>
            <div className="stats-label">Protocols</div>
          </div>
          <div className="stats-card" data-testid="stat-toolkits">
            <div className="stats-number">{stats?.toolkit_count || 0}</div>
            <div className="stats-label">Toolkits</div>
          </div>
          <div className="stats-card" data-testid="stat-guides">
            <div className="stats-number">{stats?.guide_count || 0}</div>
            <div className="stats-label">Data Guides</div>
          </div>
          <div className="stats-card" data-testid="stat-contacts">
            <div className="stats-number">{stats?.contact_count || 0}</div>
            <div className="stats-label">Contact Submissions</div>
          </div>
        </div>
      </div>

      {/* Storage Configuration */}
      <div>
        <h3 className="text-lg font-bold text-slate-900 mb-4">File Storage</h3>
        <div 
          className={`p-4 rounded-sm border ${
            stats?.storage?.storage_type === "s3" 
              ? "bg-emerald-50 border-emerald-200" 
              : "bg-blue-50 border-blue-200"
          }`} 
          data-testid="storage-info"
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              stats?.storage?.storage_type === "s3" ? "bg-emerald-500" : "bg-blue-500"
            }`}>
              {stats?.storage?.storage_type === "s3" ? (
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              ) : (
                <FileText className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <p className="font-medium text-slate-900">
                {stats?.storage?.storage_type === "s3" ? "AWS S3 Storage" : "Local File Storage"}
              </p>
              <p className="text-sm text-slate-600">
                {stats?.storage?.storage_type === "s3" 
                  ? `Bucket: ${stats?.storage?.s3_bucket} (${stats?.storage?.s3_region})`
                  : "Files stored locally on server"}
              </p>
            </div>
          </div>
          {stats?.storage?.storage_type !== "s3" && (
            <p className="text-xs text-slate-500 mt-3 border-t border-blue-200 pt-3">
              To enable S3: Set STORAGE_TYPE=&quot;s3&quot; and add AWS credentials in backend/.env
            </p>
          )}
        </div>
      </div>

      {/* Email Notifications */}
      <div>
        <h3 className="text-lg font-bold text-slate-900 mb-4">Email Notifications</h3>
        <div 
          className={`p-4 rounded-sm border ${
            stats?.email?.email_enabled 
              ? "bg-emerald-50 border-emerald-200" 
              : "bg-slate-50 border-slate-200"
          }`} 
          data-testid="email-info"
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              stats?.email?.email_enabled ? "bg-emerald-500" : "bg-slate-400"
            }`}>
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-slate-900">
                {stats?.email?.email_enabled ? "Email Notifications Enabled" : "Email Notifications Disabled"}
              </p>
              <p className="text-sm text-slate-600">
                {stats?.email?.email_enabled 
                  ? `Notifications sent to: ${stats?.email?.notification_email}`
                  : "Contact form submissions stored locally only"}
              </p>
            </div>
          </div>
          {!stats?.email?.email_enabled && (
            <p className="text-xs text-slate-500 mt-3 border-t border-slate-200 pt-3">
              To enable: Set EMAIL_ENABLED=&quot;true&quot; and add RESEND_API_KEY, NOTIFICATION_EMAIL in backend/.env
            </p>
          )}
        </div>
      </div>

      {/* Engagement Stats */}
      <div>
        <h3 className="text-lg font-bold text-slate-900 mb-4">Engagement</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="stats-card">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-600">Protocol Views</span>
            </div>
            <div className="stats-number">{stats?.total_protocol_views || 0}</div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-2 mb-2">
              <Download className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-600">Protocol Downloads</span>
            </div>
            <div className="stats-number">{stats?.total_protocol_downloads || 0}</div>
          </div>
        </div>
      </div>

      {/* Recent Contacts */}
      <div>
        <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Contacts</h3>
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {data.contacts.length > 0 ? (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Interest</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {data.contacts.slice(0, 5).map((contact) => (
                  <tr key={contact.id}>
                    <td className="px-4 py-3 text-sm text-slate-900">{contact.name}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{contact.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{contact.service_interest || "-"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        contact.status === "new" 
                          ? "bg-blue-100 text-blue-700" 
                          : "bg-slate-100 text-slate-600"
                      }`}>
                        {contact.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="p-4 text-center text-slate-500">No contacts yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
